import { useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { Input, Popover, PopoverContent, PopoverTrigger } from '@teach/ui'
import { cn } from '@/lib/utils'
import { isArticleColor } from '@/features/instructions/lib/articleStyle'
import { useMessages } from '@/i18n'
import { messages } from './messages'

const PRESETS = [
  '#ffffff',
  '#000000',
  '#1f2937',
  '#6b7280',
  '#fef3c7',
  '#fde68a',
  '#fcd34d',
  '#f59e0b',
  '#fecaca',
  '#fca5a5',
  '#f87171',
  '#ef4444',
  '#bbf7d0',
  '#86efac',
  '#4ade80',
  '#22c55e',
  '#bfdbfe',
  '#93c5fd',
  '#60a5fa',
  '#3b82f6',
  '#e9d5ff',
  '#c4b5fd',
  '#a78bfa',
  '#8b5cf6',
  '#fbcfe8',
  '#f9a8d4',
  '#f472b6',
  '#ec4899',
]

interface Props {
  value: string | null
  onChange: (c: string) => void
  disabled?: boolean
}

export const TitleColorPicker = ({ value, onChange, disabled = false }: Props) => {
  const m = useMessages(messages)
  const v = value ?? '#ffffff'
  // Свой текст поля: недопечатанный `#ff` не уходит в форму (api отбил бы его 422).
  const [text, setText] = useState<string | null>(null)
  const shown = text ?? v
  const trimmedShown = shown.trim()
  const invalid = trimmedShown !== '' && !isArticleColor(trimmedShown)
  const commit = (next: string) => {
    setText(next)
    // api обрезает пробелы до проверки: валидируем и шлём обрезанное, пробелы = пусто.
    const trimmed = next.trim()
    if (trimmed === '' || isArticleColor(trimmed)) onChange(trimmed)
  }
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-medium leading-none">{m.titleColor}</h3>

      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className="h-9 w-9 rounded border border-border disabled:pointer-events-none disabled:opacity-50"
              style={{ background: v }}
              aria-label={m.pickTitleColor}
            />
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3">
            <HexColorPicker
              color={v}
              onChange={(c) => {
                setText(null)
                onChange(c)
              }}
            />
          </PopoverContent>
        </Popover>
        <Input
          value={shown}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setText(null)}
          aria-invalid={invalid}
          placeholder="#FFFFFF"
          disabled={disabled}
          className="font-mono w-32"
        />
      </div>
      {invalid ? <p className="text-xs text-destructive">{m.colorFormat}</p> : null}
      <div className="grid gap-1.5" style={{ gridTemplateColumns: 'repeat(14, minmax(0, 1fr))' }}>
        {PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => {
              setText(null)
              onChange(c)
            }}
            disabled={disabled}
            className={cn(
              'w-full aspect-square rounded-md border-2 transition-transform hover:scale-110 disabled:pointer-events-none disabled:opacity-50',
              v.toLowerCase() === c.toLowerCase() ? 'border-white' : 'border-border',
            )}
            style={{ background: c }}
            aria-label={m.colorN(c)}
          />
        ))}
      </div>
    </div>
  )
}
